import React from 'react';
import { Lead } from '../types';

interface TableProps {
  leads: Lead[];
  filterSize: string;
  onFilterSizeChange: (value: string) => void;
  filterScore: string;
  onFilterScoreChange: (value: string) => void;
  filterStatus: string;
  onFilterStatusChange: (value: string) => void;
  filterFacebook: string;
  onFilterFacebookChange: (value: string) => void;
  showOnlyCatchOfTheDay: boolean;
  onToggleCatchOfTheDay: (value: boolean) => void;
  onSelectLead: (lead: Lead) => void;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleSelectAll?: (ids: string[]) => void;
  batchActions?: React.ReactNode;
}

const LeadTable: React.FC<TableProps> = ({ 
  leads, showOnlyCatchOfTheDay, onToggleCatchOfTheDay, onSelectLead, selectedIds,
  onToggleSelect, onToggleSelectAll, batchActions
}) => {

  const handleExportCSV = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (leads.length === 0) return;
    const headers = ["Business", "Industry", "Province", "Web", "Contact", "Score"];
    const rows = leads.map(l => [l.businessName, l.industry, l.province, l.website, l.contactNumber, l.vettingScore]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "rif_scout_batch.csv";
    link.click();
  };

  return (
    <div className="bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
      <div className="p-5 border-b border-gray-100 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 bg-gray-50/50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-red-600 text-white rounded flex items-center justify-center shadow-lg">
            <i className="fa-solid fa-fire text-xs"></i>
          </div>
          <div>
            <h3 className="font-black text-black text-sm uppercase tracking-widest italic leading-none">Scout Furnace</h3>
            <p className="text-[10px] text-gray-500 font-bold mt-1 tracking-tight">Vetting South African businesses for digital scale.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
           {batchActions && selectedIds && selectedIds.size > 0 && batchActions}
           <button onClick={() => onToggleCatchOfTheDay(!showOnlyCatchOfTheDay)}
             className={`px-3 py-2 text-[9px] font-black uppercase tracking-widest rounded-lg border transition-all ${
               showOnlyCatchOfTheDay ? 'bg-red-600 text-white border-red-700 shadow-lg shadow-red-200' : 'bg-white text-gray-400 border-gray-200 hover:border-red-300 hover:text-red-500'
             }`}>
             <i className="fa-solid fa-bolt mr-1"></i> Gaps Only
           </button>
           <button onClick={handleExportCSV} disabled={leads.length === 0}
             className="px-4 py-2 text-[9px] font-black uppercase bg-black text-white rounded-lg hover:bg-gray-900 transition-all flex items-center gap-2 shadow-sm disabled:opacity-30">
              <i className="fa-solid fa-file-csv"></i> Export CSV
           </button>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-black text-gray-400 text-[9px] uppercase font-black tracking-[0.1em]">
              {onToggleSelectAll && <th className="px-6 py-4 w-10"><input type="checkbox" className="accent-red-600" checked={leads.length > 0 && leads.every(l => selectedIds?.has(l.id))} onChange={() => onToggleSelectAll(leads.map(l => l.id))} /></th>}
              <th className="px-6 py-4">Identification & Digital Hub</th>
              <th className="px-6 py-4">Sector / Scale</th>
              <th className="px-6 py-4 text-center">Score</th>
              <th className="px-6 py-4 text-right pr-10">Intel</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {leads.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-20 text-center">
                  <div className="text-gray-300 mb-2"><i className="fa-solid fa-radar text-4xl animate-pulse"></i></div>
                  <p className="text-[10px] font-black uppercase text-gray-400">The furnace is cooling. Start a Batch Forge.</p>
                </td>
              </tr>
            ) : leads.map((lead) => (
              <tr key={lead.id} onClick={() => onSelectLead(lead)}
                className={`group cursor-pointer hover:bg-red-50/40 transition-all ${selectedIds?.has(lead.id) ? 'bg-red-50' : ''}`}>
                {onToggleSelect && <td className="px-6 py-5" onClick={e => e.stopPropagation()}><input type="checkbox" className="accent-red-600" checked={selectedIds?.has(lead.id)} onChange={() => onToggleSelect(lead.id)} /></td>}
                <td className="px-6 py-5">
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <span className="font-black text-black text-sm uppercase italic tracking-tight">{lead.businessName}</span>
                      {lead.isCatchOfTheDay && <span title="High Priority Gap" className="w-2 h-2 rounded-full bg-red-600 animate-pulse shadow-[0_0_8px_rgba(220,38,38,0.5)]"></span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-3" onClick={e => e.stopPropagation()}>
                      <a href={lead.website !== 'NONE' ? (lead.website.startsWith('http') ? lead.website : `https://${lead.website}`) : '#'} target="_blank" className={`text-[10px] font-black uppercase transition-all flex items-center gap-1.5 px-2 py-0.5 rounded ${lead.website === 'NONE' ? 'text-gray-300 bg-gray-100 cursor-not-allowed' : 'text-red-500 bg-red-50 hover:bg-red-600 hover:text-white'}`}>
                        <i className="fa-solid fa-link text-[8px]"></i> Website
                      </a>
                      <div className="flex items-center gap-2 px-2 py-0.5 bg-gray-100 rounded">
                        {lead.facebookUrl ? (
                          <a href={lead.facebookUrl} target="_blank" className="text-blue-600 hover:scale-125 transition-transform" title="Facebook"><i className="fa-brands fa-facebook text-[12px]"></i></a>
                        ) : <i className="fa-brands fa-facebook text-gray-300 text-[12px]" title="No Facebook found"></i>}
                        
                        {lead.instagramUrl ? (
                          <a href={lead.instagramUrl} target="_blank" className="text-pink-600 hover:scale-125 transition-transform" title="Instagram"><i className="fa-brands fa-instagram text-[12px]"></i></a>
                        ) : <i className="fa-brands fa-instagram text-gray-300 text-[12px]" title="No Instagram found"></i>}
                        
                        {lead.linkedInUrl ? (
                          <a href={lead.linkedInUrl} target="_blank" className="text-blue-800 hover:scale-125 transition-transform" title="LinkedIn"><i className="fa-brands fa-linkedin text-[12px]"></i></a>
                        ) : <i className="fa-brands fa-linkedin text-gray-300 text-[12px]" title="No LinkedIn found"></i>}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-5">
                  <span className="inline-block bg-black text-white px-2 py-0.5 rounded-md text-[8px] font-black uppercase mb-1">{lead.industry}</span>
                  <div className="text-[9px] text-gray-400 font-bold uppercase tracking-tight">{lead.province} | {lead.companySize?.split(' ')[0] || "SME"}</div>
                </td>
                <td className="px-6 py-5 text-center">
                  <div className={`text-sm font-black inline-flex items-center justify-center w-9 h-9 rounded-xl border-2 transition-all ${lead.vettingScore >= 8 ? 'text-red-600 border-red-600 bg-red-50 scale-110 shadow-lg shadow-red-100' : 'text-black border-gray-100 bg-gray-50'}`}>
                    {lead.vettingScore}
                  </div>
                </td>
                <td className="px-6 py-5 text-right pr-10">
                  <div className="bg-gray-100 group-hover:bg-red-600 group-hover:text-white w-8 h-8 rounded-lg inline-flex items-center justify-center transition-all group-hover:rotate-90">
                    <i className="fa-solid fa-chevron-right text-[10px]"></i>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeadTable;